import { useEffect, useState } from "react";
import { useLang } from "@/lib/LanguageContext";

/**
 * InstallApp
 * Floating "Install App" pill that appears when the browser fires
 * `beforeinstallprompt` (Android Chrome, Edge, desktop Chrome).
 * On iOS Safari we fall back to a small instruction sheet.
 */
export default function InstallApp() {
    const { lang } = useLang();
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [iosHint, setIosHint] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    const copy = {
        en: {
            install: "Install App",
            installing: "Installing…",
            installed: "Installed",
            iosTitle: "Add to Home Screen",
            iosBody:
                "Tap the Share icon in Safari, then choose “Add to Home Screen” to install Ahmetography.",
            iosClose: "Got it",
            dismiss: "Not now",
        },
        tr: {
            install: "Uygulamayı Yükle",
            installing: "Yükleniyor…",
            installed: "Yüklendi",
            iosTitle: "Ana Ekrana Ekle",
            iosBody:
                "Safari'de Paylaş simgesine dokunun, ardından “Ana Ekrana Ekle”yi seçin.",
            iosClose: "Tamam",
            dismiss: "Şimdi değil",
        },
    }[lang || "en"];

    useEffect(() => {
        // Detect already-installed (standalone) mode
        const standalone =
            window.matchMedia &&
            window.matchMedia("(display-mode: standalone)").matches;
        const iosStandalone = window.navigator.standalone === true;
        if (standalone || iosStandalone) {
            setIsStandalone(true);
            return;
        }

        // iOS Safari does not support beforeinstallprompt
        const ua = window.navigator.userAgent || "";
        const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);

        if (isIOS && isSafari) {
            // Only show iOS hint if user hasn't dismissed it
            const dismissed = localStorage.getItem("aw_ios_install_dismissed");
            if (!dismissed) {
                const t = setTimeout(() => setIosHint(true), 2500);
                return () => clearTimeout(t);
            }
        }

        const handler = (e) => {
            e.preventDefault();
            setDeferred(e);
            setVisible(true);
        };
        window.addEventListener("beforeinstallprompt", handler);

        const installed = () => {
            setVisible(false);
            setDeferred(null);
            setIsStandalone(true);
        };
        window.addEventListener("appinstalled", installed);

        return () => {
            window.removeEventListener("beforeinstallprompt", handler);
            window.removeEventListener("appinstalled", installed);
        };
    }, []);

    const onInstall = async () => {
        if (!deferred) return;
        deferred.prompt();
        try {
            const choice = await deferred.userChoice;
            if (choice.outcome === "accepted") {
                setVisible(false);
            }
        } finally {
            setDeferred(null);
        }
    };

    const dismiss = () => {
        setVisible(false);
        localStorage.setItem("aw_pwa_dismissed", String(Date.now()));
    };

    const dismissIos = () => {
        setIosHint(false);
        localStorage.setItem("aw_ios_install_dismissed", "1");
    };

    if (isStandalone) return null;

    return (
        <>
            {visible && (
                <div
                    data-testid="install-app-banner"
                    className="fixed left-4 bottom-4 md:left-6 md:bottom-6 z-[60] bg-navy text-ivory shadow-2xl border border-ivory/10 max-w-sm"
                >
                    <div className="p-5 flex items-start gap-4">
                        <div className="h-12 w-12 flex-shrink-0 bg-ivory/5 flex items-center justify-center">
                            <img
                                src="/icons/logo-192.png"
                                alt=""
                                className="h-10 w-10 object-contain"
                                style={{ mixBlendMode: "lighten" }}
                            />
                        </div>
                        <div className="flex-1">
                            <p className="font-serif text-xl leading-tight">
                                Ahmetography
                            </p>
                            <p className="text-xs text-ivory/65 mt-1 leading-relaxed">
                                {lang === "tr"
                                    ? "Stüdyoyu cihazınıza yükleyin — daha hızlı, çevrimdışı da çalışır."
                                    : "Install the studio to your device — faster, even offline."}
                            </p>
                            <div className="mt-4 flex items-center gap-3">
                                <button
                                    type="button"
                                    data-testid="install-app-button"
                                    onClick={onInstall}
                                    className="px-4 py-2 bg-terracotta text-ivory text-xs uppercase tracking-[0.18em]"
                                >
                                    {copy.install}
                                </button>
                                <button
                                    type="button"
                                    data-testid="install-dismiss"
                                    onClick={dismiss}
                                    className="text-xs uppercase tracking-[0.18em] text-ivory/65 hover:text-ivory"
                                >
                                    {copy.dismiss}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {iosHint && (
                <div
                    data-testid="ios-install-hint"
                    className="fixed left-4 right-4 bottom-4 md:left-auto md:right-6 md:bottom-6 z-[60] bg-navy text-ivory shadow-2xl border border-ivory/10 md:max-w-sm"
                >
                    <div className="p-5">
                        <div className="flex items-start justify-between gap-3">
                            <p className="font-serif text-xl">{copy.iosTitle}</p>
                            <button
                                type="button"
                                onClick={dismissIos}
                                aria-label="Close"
                                className="text-ivory/65 hover:text-ivory text-2xl leading-none"
                            >
                                ×
                            </button>
                        </div>
                        <p className="text-sm text-ivory/70 mt-3 leading-relaxed">
                            {copy.iosBody}
                        </p>
                        <button
                            type="button"
                            onClick={dismissIos}
                            className="mt-4 px-4 py-2 bg-terracotta text-ivory text-xs uppercase tracking-[0.18em]"
                            data-testid="ios-install-dismiss"
                        >
                            {copy.iosClose}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
